import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  PhoneVerification,
  Modal,
  Text,
  Button,
  Icon,
  CheckboxField,
  useApplicationNotifications,
  utils as tkUtils,
} from '@adc/ui-core'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const {
  PhoneVerificationReduxForm: CorePhoneVerificationReduxForm,
  providers,
  ...rest
} = PhoneVerification

export const MorpheusPhoneVerificationReduxForm = ({
  shouldDisplayDelete,
  onDeletePhone,
  ...props
}) => {
  const notifications = useApplicationNotifications()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(false)
  const [shouldDeleteAllPhones, setShouldDeleteAllPhones] = useState(false)

  const onAllPhonesDeleteChange = (_, { checked }) => {
    setShouldDeleteAllPhones(checked)
  }

  const onDeleteClick = () => {
    setShowDeleteModal(true)
  }

  const onClose = () => {
    setShowDeleteModal(false)
    setShouldDeleteAllPhones(false)
  }
  const HtmlRenderer = ({ html }) => {
    return <div dangerouslySetInnerHTML={{ __html: html }} />
  }
  HtmlRenderer.propTypes = {
    html: PropTypes.string.isRequired,
  }
  const onConfirmDeleteLatest = async () => {
    const { formValues, latestPhone } = props
    setIsConfirmDisabled(true)

    try {
      const response = await providers.removePhone({
        partyId: formValues.party_id,
        phoneId: latestPhone.phone_id,
      })

      const data = await response
      if (!response.ok) {
        const errors = data.body?.errors
        if (errors && errors.length > 0) {
          const error = new Error('An error occurred')
          error.body = { errors }
          throw error
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      if (data.body && data.body.errors && data.body.errors.length > 0) {
        const error = new Error('An error occurred')
        error.body = { errors: data.body.errors }
        throw error
      }

      onDeletePhone(latestPhone)
      notifications.overlayBanner.open({
        role: 'info',
        title: 'Phone removed successfully.',
      })
    } catch (error) {
      let title = 'Unable to do any update'
      let message = 'Failed to remove phone.'
      if (error.body?.errors && error.body.errors.length > 0) {
        const firstError = error.body.errors[0]
        const errorMessage = firstError.message
        const specificErrorCode = firstError.extensions?.specificErrorCode
        // Handle specific error codes
        switch (specificErrorCode) {
          case 'PHONE_DELETION_NOT_VALID': {
            const errorMessages = errorMessage.split(',').map(err => err.trim())
            // remvoing the status code at the beginning of the error message
            const cleanedErrorMessages = errorMessages.map(err =>
              err.replace(/^\d{3}:\s*/, '')
            )
            const detailedErrors = cleanedErrorMessages
              .map((err, index) => `${index + 1}. ${err}`)
              .join('<br>')
            title = `Unable to Delete Phone Number</br>`
            message = `We are unable to delete the phone number as you have open transactions with us.<br><br>Here are the details:<br>${detailedErrors}`
            break
          }
          default:
            message = errorMessage || 'An unknown error occurred.'
        }
      } else {
        message = error.message || 'An unknown error occurred.'
      }

      // Show the notification
      notifications.overlayBanner.open({
        role: 'error',
        title: <HtmlRenderer html={title} />,
        message: <HtmlRenderer html={message} />,
      })
    } finally {
      setIsConfirmDisabled(false)
      onClose()
    }
  }

  const onConfirmDelete = () =>
    shouldDeleteAllPhones ? onConfirmDeleteAll() : onConfirmDeleteLatest()

  const onConfirmDeleteAll = async () => {
    const { formValues } = props

    const partyId = formValues.party_id
    const phoneIds = formValues.phones.map(p => p.phone_id)

    setIsConfirmDisabled(true)

    try {
      const responses = await Promise.all(
        phoneIds.map(phoneId =>
          providers.removePhone({
            partyId,
            phoneId,
          })
        )
      )

      responses.forEach(res => {
        if (res.body.errors && res.body.errors.length > 0) {
          throw res.body.errors[0]
        }
      })

      onDeletePhone(null)
      notifications.overlayBanner.open({
        role: 'info',
        title: 'Phones removed successfully.',
      })
    } catch (err) {
      notifications.overlayBanner.open({
        role: 'error',
        title: <HtmlRenderer html='Unable to delete phone number' />,
        message: <HtmlRenderer html={err.message} />,
      })
    } finally {
      setIsConfirmDisabled(false)
      onClose()
    }
  }

  const priorPhones =
    props.formValues.phones?.filter(
      phone => phone.phone_id !== props.latestPhone?.phone_id
    ) ?? []

  const elmId = 'delete_phone'
  return (
    <>
      <CorePhoneVerificationReduxForm
        {...props}
        onDeleteClick={shouldDisplayDelete ? onDeleteClick : null}
      />
      {showDeleteModal && (
        <Modal
          size='tiny'
          open
          title='Are you sure you want to delete?'
          elmId={elmId}
          closeIcon={
            <Icon
              className='close'
              data-elm-id={`${elmId}_close_button`}
              name='times'
              iconStyle='regular'
              fitted
              onClick={onClose}
            />
          }
        >
          <Modal.Content>
            <Text.Body>
              The primary phone number will be deleted. Are you sure?
            </Text.Body>
            {priorPhones.length > 0 && (
              <>
                <div className={cx('additional-entries, u-mt-3')}>
                  <Text
                    content='There are additional prior entries:'
                    color='gray'
                  />
                  <ul className={cx('u-mt-1')}>
                    {priorPhones.map((phone, idx) => (
                      <li className={cx('u-mt-1', 'u-ml-2')} key={idx}>
                        <Text
                          content={tkUtils.formatters.formatPhoneNumber(
                            phone.number
                          )}
                          color='gray'
                        />
                      </li>
                    ))}
                  </ul>
                </div>
                <CheckboxField
                  className={cx('delete-all-checkbox')}
                  onChange={onAllPhonesDeleteChange}
                  label='Delete these entries as well'
                  elmId='delete_all_phones'
                  name='deleteAll'
                  role='action'
                  checked={shouldDeleteAllPhones}
                  compact={false}
                />
              </>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={onClose} elmId={`${elmId}_cancel_action_btn`}>
              Cancel
            </Button>
            <Button
              role='primary'
              onClick={onConfirmDelete}
              elmId={`${elmId}_confirm_action_btn`}
              disabled={isConfirmDisabled}
            >
              Delete
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  )
}

MorpheusPhoneVerificationReduxForm.propTypes = {
  shouldDisplayDelete: PropTypes.bool,
  onDeletePhone: PropTypes.func,
  formValues: PropTypes.shape({
    party_id: PropTypes.string,
  }),
  latestPhone: PropTypes.shape({
    phone_id: PropTypes.string,
  }),
}

export default {
  PhoneVerificationReduxForm: MorpheusPhoneVerificationReduxForm,
  ...rest,
}
